import {
  lerp,
  glidePosition,
  metersBetween,
  startGlide as startGlideFor,
  settledGlide as settledGlideFor,
  type EntityGlide,
} from './entityGlide.js';

// Bus-specific tuning for the shared entity-interpolation primitives.
//
// The MTA bus GTFS-rt feed reports each bus's position roughly every poll but its
// GPS `timestamp` is stale by an unknown, variable amount — so dead-reckoning from
// it made buses leap ahead then freeze. Instead we glide the drawn marker from
// where it is to the latest reported position. The glide lasts a hair longer than
// the ~30s poll cadence, so a bus is essentially always mid-glide when the next
// report lands and gets redirected toward the new target — it (almost) never
// reaches the target and freezes. Keeping it CLOSE to the cadence matters: a much
// longer window renders buses noticeably slower than they actually move. (Buses are
// genuinely slow — ~5 m/s — so motion only reads as motion when zoomed into a
// neighborhood; at city-wide zoom it is sub-pixel-per-second and looks still.)

export const GLIDE_MS = 33_000; // just over the ~30s poll cadence: real-speed, rarely stalls
export const SNAP_METERS = 1_500; // jumps beyond this teleport (trip reassign / GPS reacquire)
export const JITTER_METERS = 3; // ignore sub-GPS-noise wiggle so parked buses sit still

/** An in-progress bus glide (alias of the shared {@link EntityGlide}). */
export type BusGlide = EntityGlide;

/** Start a fresh bus glide from `from` toward `to`, lasting one (over-long) glide window. */
export function startGlide(from: [number, number], to: [number, number], startMs: number): BusGlide {
  return startGlideFor(from, to, startMs, GLIDE_MS);
}

/** A bus glide that holds a single point (first sight, or a snap/teleport). */
export function settledGlide(at: [number, number], startMs: number): BusGlide {
  return settledGlideFor(at, startMs, GLIDE_MS);
}

export { lerp, glidePosition, metersBetween };
